import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

/**
 * The dashboard's Supabase client is created from RUNTIME configuration served
 * by the platform Worker at /api/platform/config. There is deliberately no
 * build-time fallback: if the config is missing the app shows an explicit error
 * screen instead of quietly connecting to the wrong project.
 */

export interface PlatformConfig {
  supabase_url: string;
  supabase_anon_key: string;
  /** e.g. "adop.app" (prod) or "localtest.me" (dev). */
  storefront_base_domain: string;
}

let client: SupabaseClient<Database> | null = null;
let platformConfig: PlatformConfig | null = null;

export function initSupabase(config: PlatformConfig): SupabaseClient<Database> {
  platformConfig = config;
  client = createClient<Database>(config.supabase_url, config.supabase_anon_key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
  return client;
}

/** Throws when called before {@link initSupabase} — that is a programming error. */
export function getSupabase(): SupabaseClient<Database> {
  if (!client) {
    throw new Error(
      'Supabase client was used before the platform configuration loaded. ' +
        'This should be impossible; check the bootstrap in main.tsx.',
    );
  }
  return client;
}

/** Runtime platform config (storefront domain etc). Loaded before the app renders. */
export function getPlatformConfig(): PlatformConfig {
  if (!platformConfig) {
    throw new Error(
      'Platform configuration was used before it loaded. ' +
        'This should be impossible; check the bootstrap in main.tsx.',
    );
  }
  return platformConfig;
}

interface ConfigErrorBody {
  error?: { message?: string };
}

/** Fetches the public Supabase config from the same-origin platform Worker. */
export async function loadPlatformConfig(): Promise<PlatformConfig> {
  let response: Response;
  try {
    response = await fetch('/api/platform/config', { headers: { accept: 'application/json' } });
  } catch {
    throw new Error(
      'Could not reach the platform API at /api/platform/config. ' +
        'Is the adop-platform Worker running? (./start.sh, then check .adop/dev.log)',
    );
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ConfigErrorBody | null;
    throw new Error(
      body?.error?.message ??
        `The platform API is not configured (HTTP ${response.status}). See docs/configuration.md.`,
    );
  }

  const body = (await response.json().catch(() => null)) as Partial<PlatformConfig> | null;
  if (!body?.supabase_url || !body?.supabase_anon_key || !body?.storefront_base_domain) {
    throw new Error(
      'The platform API returned an incomplete configuration (/api/platform/config). ' +
        'See docs/configuration.md.',
    );
  }

  return {
    supabase_url: body.supabase_url,
    supabase_anon_key: body.supabase_anon_key,
    storefront_base_domain: body.storefront_base_domain,
  };
}
